import { useEffect, useState, useCallback } from "react";
import api, { formatApiError, eur } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Package, Trash2, Pencil, MoveHorizontal, Gauge, Zap } from "lucide-react";

const empty = { model: "", category: "", description: "", stroke_mm: "", force_n: "", speed_mms: "", price: "" };

export default function Products() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => api.get("/products").then((r) => setProducts(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ ...p }); setEditId(p.id); setOpen(true); };

  const save = async () => {
    if (!form.model.trim()) { toast.error("Il modello è obbligatorio"); return; }
    const payload = { ...form, stroke_mm: Number(form.stroke_mm) || 0, force_n: Number(form.force_n) || 0, speed_mms: Number(form.speed_mms) || 0, price: Number(form.price) || 0 };
    try {
      if (editId) await api.put(`/products/${editId}`, payload);
      else await api.post("/products", payload);
      toast.success(editId ? "Prodotto aggiornato" : "Prodotto creato");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => { await api.delete(`/products/${id}`); toast.success("Prodotto eliminato"); load(); };

  return (
    <div className="p-10">
      <PageHeader subtitle="CATALOGO" title="Catalogo Attuatori"
        action={<Button data-testid="new-product-btn" onClick={openNew} className="bg-[#0052CC] hover:bg-[#003D99] rounded-sm"><Plus className="w-4 h-4 mr-2" />Nuovo Modello</Button>} />

      {products.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-sm py-20 text-center">
          <Package className="w-10 h-10 text-slate-300 mx-auto mb-4" />
          <div className="font-display font-bold text-lg text-slate-900">Catalogo vuoto</div>
          <p className="text-sm text-slate-500 mt-1">Aggiungi i tuoi modelli di attuatori lineari.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {products.map((p) => (
            <div key={p.id} data-testid={`product-${p.id}`} className="bg-white border border-slate-200 rounded-sm p-6 hover:shadow-sm transition-shadow duration-200">
              <div className="flex items-start justify-between mb-4">
                <div>
                  {p.category && <div className="font-mono-label text-[9px] text-[#0052CC] mb-1">{p.category}</div>}
                  <div className="font-display font-bold text-lg text-slate-900">{p.model}</div>
                </div>
                <div className="flex gap-1">
                  <button data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)} className="text-slate-300 hover:text-[#0052CC] transition-colors duration-200"><Pencil className="w-4 h-4" /></button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild><button data-testid={`del-product-${p.id}`} className="text-slate-300 hover:text-red-500 transition-colors duration-200"><Trash2 className="w-4 h-4" /></button></AlertDialogTrigger>
                    <AlertDialogContent className="rounded-sm">
                      <AlertDialogHeader><AlertDialogTitle>Eliminare {p.model}?</AlertDialogTitle><AlertDialogDescription>Azione irreversibile.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel><AlertDialogAction onClick={() => del(p.id)} className="bg-red-600 hover:bg-red-700 rounded-sm">Elimina</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              {p.description && <p className="text-sm text-slate-500 mb-4">{p.description}</p>}
              <div className="grid grid-cols-3 gap-2 mb-4">
                {[[MoveHorizontal, "Corsa", `${p.stroke_mm} mm`], [Zap, "Forza", `${p.force_n} N`], [Gauge, "Velocità", `${p.speed_mms} mm/s`]].map(([Icon, l, v], i) => (
                  <div key={i} className="border border-slate-100 rounded-sm p-2 text-center">
                    <Icon className="w-4 h-4 text-slate-400 mx-auto mb-1" />
                    <div className="font-mono-label text-[8px] text-slate-400">{l}</div>
                    <div className="text-xs font-semibold text-slate-800">{v}</div>
                  </div>
                ))}
              </div>
              <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
                <span className="font-mono-label text-[10px] text-slate-400">PREZZO</span>
                <span className="font-display font-bold text-lg text-[#0052CC]">{eur(p.price)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Modifica Modello" : "Nuovo Modello"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2"><Label className="font-mono-label text-[10px] text-slate-500">Modello *</Label>
              <Input data-testid="product-model-input" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="mt-1.5 rounded-sm" placeholder="es. LA-200-DC" /></div>
            <div className="col-span-2"><Label className="font-mono-label text-[10px] text-slate-500">Categoria</Label>
              <Input data-testid="product-category-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1.5 rounded-sm" placeholder="es. Attuatore elettrico" /></div>
            <div><Label className="font-mono-label text-[10px] text-slate-500">Corsa (mm)</Label>
              <Input data-testid="product-stroke-input" type="number" value={form.stroke_mm} onChange={(e) => setForm({ ...form, stroke_mm: e.target.value })} className="mt-1.5 rounded-sm" /></div>
            <div><Label className="font-mono-label text-[10px] text-slate-500">Forza (N)</Label>
              <Input data-testid="product-force-input" type="number" value={form.force_n} onChange={(e) => setForm({ ...form, force_n: e.target.value })} className="mt-1.5 rounded-sm" /></div>
            <div><Label className="font-mono-label text-[10px] text-slate-500">Velocità (mm/s)</Label>
              <Input data-testid="product-speed-input" type="number" value={form.speed_mms} onChange={(e) => setForm({ ...form, speed_mms: e.target.value })} className="mt-1.5 rounded-sm" /></div>
            <div><Label className="font-mono-label text-[10px] text-slate-500">Prezzo (€)</Label>
              <Input data-testid="product-price-input" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1.5 rounded-sm" /></div>
            <div className="col-span-2"><Label className="font-mono-label text-[10px] text-slate-500">Descrizione</Label>
              <Textarea data-testid="product-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 rounded-sm" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Annulla</Button>
            <Button data-testid="save-product-btn" onClick={save} className="bg-[#0052CC] hover:bg-[#003D99] rounded-sm">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
